/**
 * OpenQuizRun — публичный экран соло-прохождения открытого квиза `/q/<slug>`
 * (Фаза 10, И11/С3; план §C6.2). Без auth: посетитель вводит обязательный ник
 * (санитайзится и на клиенте — trim/30, и на сервере) и проходит квиз сам,
 * листая вопросы **свайпом** (touch), кнопками «←/→» или стрелками клавиатуры.
 *
 * Стадии: loading → gate(мета+ник) → quiz(active) → finish; ошибки-экраны:
 * notFound (404), closed (лимит попыток), gone (попытка/квиз умерли на лету, RC6),
 * resume-оффер при живой попытке в sessionStorage (F5 не теряет прогресс).
 *
 * Анти-чит (§C4.1): до финиша НЕТ reveal — карточки рендерятся существующими
 * под-view (Choice/Poll/Open/Match/Order/Word) с reveal=null; correct-данные
 * приходят ТОЛЬКО с POST finish и показываются на итоговом экране. Word в соло =
 * просто input (WordView answer-вариант), облако не нужно.
 *
 * Ответ уходит явным сабмитом (клик по опции choice/poll — авто; кнопка
 * «Ответить» для open/match/order/word — компоненты view), fire-and-forget:
 * идемпотентность PK на сервере прикрывает повторы. a11y: стрелки не работают в
 * полях ввода, фокус на карточке при перелистывании, prefers-reduced-motion
 * гасит transform-переход глобальным слоем index.css.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  finishOpenAttempt,
  fetchOpenAttemptState,
  fetchOpenQuizMeta,
  startOpenAttempt,
  submitOpenAnswer,
  type OpenFinishResp,
  type OpenKindData,
  type OpenQuestion,
  type OpenQuizMeta,
} from '../api';
import { clearOpenAttempt, loadOpenAttempt, saveOpenAttempt } from '../openAttemptSession';
import {
  KINDS,
  makeAnswerPayload,
  type Kind,
  type KindData,
  type PlayerAnswer,
  type QuestionPayload,
} from '../protocol';
import { Link } from '../components/ui';
import { FormulaText } from '../components/FormulaText';
import { CodeBlock } from '../components/CodeBlock';
import { ChoiceView } from '../components/views/ChoiceView';
import { PollView } from '../components/views/PollView';
import { OpenView } from '../components/views/OpenView';
import { MatchView } from '../components/views/MatchView';
import { OrderView } from '../components/views/OrderView';
import { WordView } from '../components/views/WordView';
import { MultiView } from '../components/views/MultiView';
import type { QuestionBodyProps } from '../components/views/types';
import { navigate } from '../router';

/** Пороги свайпа (§C6.2): дельта X ≥ 50px, жест ≤ 500 мс, горизонтальность. */
const SWIPE_MIN_PX = 50;
const SWIPE_MAX_MS = 500;

interface ActiveAttempt {
  attemptId: number;
  token: string;
  totalQuestions: number;
  questions: OpenQuestion[];
}

type Stage =
  'loading' | 'gate' | 'resume' | 'quiz' | 'finish' | 'notfound' | 'closed' | 'gone' | 'error';

export interface OpenQuizRunProps {
  /** Slug из URL `/q/<slug>`. */
  slug: string;
}

/** OpenQuestion → QuestionPayload-мок для переиспользования под-view (§C6.2). */
function toQuestionPayload(q: OpenQuestion, index: number, total: number): QuestionPayload {
  const kind = (KINDS as readonly string[]).includes(q.kind) ? (q.kind as Kind) : 'choice';
  return {
    questionId: q.questionId,
    title: q.title,
    durationMs: 0,
    startedAt: 0,
    index,
    total,
    kind,
    kindData: q.kindData as KindData | undefined,
    image: q.image,
    // Фаза 16 (B3): проброс блока кода в мок QuestionPayload — CodeBlock
    // карточки соло-режима рендерится при наличии code.
    code: q.code,
    codeLang: q.codeLang,
  };
}

function optionsOf(q: OpenQuestion): string[] {
  const kd = q.kindData as OpenKindData | undefined;
  const opts = kd && Array.isArray(kd.options) ? kd.options : [];
  return opts.filter((x): x is string => typeof x === 'string');
}

function itemsOf(q: OpenQuestion): string[] {
  const kd = q.kindData as OpenKindData | undefined;
  const items = kd && Array.isArray(kd.items) ? kd.items : [];
  return items.filter((x): x is string => typeof x === 'string');
}

/** Строка reveal-ответа для итогового экрана (correct приходит только с finish). */
function revealText(
  t: (key: string, opts?: Record<string, string>) => string,
  q: OpenQuestion,
  reveal: Record<string, unknown> | undefined,
): string | null {
  if (!reveal) return null;
  switch (q.kind) {
    case 'choice': {
      const idx = reveal.correctOption;
      const options = optionsOf(q);
      if (typeof idx === 'number' && options[idx] !== undefined) {
        return t('openQuiz.revealCorrectOption', { option: options[idx] });
      }
      return null;
    }
    case 'open': {
      const accept = reveal.accept;
      if (Array.isArray(accept) && accept.every((x) => typeof x === 'string')) {
        return t('openQuiz.revealAccept', { accept: (accept as string[]).join(', ') });
      }
      return null;
    }
    case 'match': {
      const pairs = reveal.pairs;
      if (Array.isArray(pairs)) {
        const rows = pairs
          .filter((p): p is { left: string; right: string } => {
            if (typeof p !== 'object' || p === null) return false;
            const o = p as Record<string, unknown>;
            return typeof o.left === 'string' && typeof o.right === 'string';
          })
          .map((p) => t('openQuiz.revealPair', { left: p.left, right: p.right }));
        if (rows.length > 0) return rows.join('; ');
      }
      return null;
    }
    case 'order': {
      const order = reveal.correctOrder;
      const items = itemsOf(q);
      if (Array.isArray(order)) {
        const seq = order
          .filter((i): i is number => typeof i === 'number' && items[i] !== undefined)
          .map((i) => items[i]);
        if (seq.length > 0) return t('openQuiz.revealOrder', { order: seq.join(' → ') });
      }
      return null;
    }
    case 'multi': {
      // Серверный finish-reveal multi: {"correct":[...]} (1:1 с WS revealData);
      // top-level correctOptions — запасной канал (симметрия с live).
      const correct = reveal.correct ?? reveal.correctOptions;
      const options = optionsOf(q);
      if (Array.isArray(correct)) {
        const names = correct
          .filter((i): i is number => typeof i === 'number' && options[i] !== undefined)
          .map((i) => options[i]);
        if (names.length > 0)
          return t('openQuiz.revealCorrectOption', { option: names.join(', ') });
      }
      return null;
    }
    case 'run': {
      // Reveal run переиспользует correctOption (0..3, §3.1): верный угол плитки.
      const idx = reveal.correctOption;
      const kd = q.kindData as OpenKindData | undefined;
      const corners = kd && Array.isArray(kd.corners) ? kd.corners : [];
      const corner =
        typeof idx === 'number' && typeof corners[idx] === 'string' ? corners[idx] : null;
      if (corner !== null) {
        return t('openQuiz.revealCorrectOption', { option: corner });
      }
      return null;
    }
    default:
      // poll/word — правильного ответа нет (reveal отсутствует, как в live).
      return null;
  }
}

/** Компактная строка «ваш ответ» (из локального myAnswer; при resume может не быть). */
function answerText(q: OpenQuestion, answer: PlayerAnswer | undefined): string | null {
  if (!answer) return null;
  const options = optionsOf(q);
  switch (answer.kind) {
    case 'choice':
    case 'poll':
      return options[answer.option] ?? null;
    case 'open':
      return answer.data.text;
    case 'word':
      return answer.data.word;
    case 'match':
      return answer.data.pairs.map((p) => `${p.left} ↔ ${p.right}`).join('; ');
    case 'order': {
      const items = itemsOf(q);
      return answer.data.order.map((i) => items[i] ?? '?').join(' → ');
    }
    case 'multi': {
      const options = optionsOf(q);
      return answer.data.selected.map((i) => options[i] ?? '?').join(', ');
    }
  }
}

/** Ответ-тело для submitOpenAnswer из PlayerAnswer (без поля kind — его нет в openAnswerReq). */
function toAnswerBody(answer: PlayerAnswer): { option?: number; data?: Record<string, unknown> } {
  const payload = makeAnswerPayload(0, answer);
  const body: { option?: number; data?: Record<string, unknown> } = {};
  if (payload.option !== undefined) body.option = payload.option;
  if (payload.data !== undefined) {
    body.data = payload.data as unknown as Record<string, unknown>;
  }
  return body;
}

export function OpenQuizRun({ slug }: OpenQuizRunProps): JSX.Element {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>('loading');
  const [meta, setMeta] = useState<OpenQuizMeta | null>(null);
  const [nick, setNick] = useState('');
  const [startError, setStartError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [attempt, setAttempt] = useState<ActiveAttempt | null>(null);
  const [index, setIndex] = useState(0);
  const [answeredIds, setAnsweredIds] = useState<Set<number>>(new Set());
  const [myAnswers, setMyAnswers] = useState<Record<number, PlayerAnswer>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [finishResp, setFinishResp] = useState<OpenFinishResp | null>(null);
  const [resumeAnswered, setResumeAnswered] = useState<number[]>([]);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const touchStart = useRef<{ x: number; y: number; t: number } | null>(null);

  // noindex (RC12): публичные capability-ссылки не для индексации. Клиентская
  // инъекция <meta name="robots"> на время жизни экрана.
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    if (existing) return;
    const metaEl = document.createElement('meta');
    metaEl.setAttribute('name', 'robots');
    metaEl.setAttribute('content', 'noindex');
    document.head.appendChild(metaEl);
    return () => {
      metaEl.remove();
    };
  }, []);

  // Стартовая инициализация: мета квиза + проверка живой попытки (resume).
  const init = useCallback(async () => {
    setStage('loading');
    let m: OpenQuizMeta | null = null;
    try {
      m = await fetchOpenQuizMeta(slug);
      setMeta(m);
    } catch (e) {
      if (e instanceof Error && 'status' in e && (e as { status: number }).status === 404) {
        setStage('notfound');
        return;
      }
      setStage('error');
      return;
    }
    if (m.status === 'closed') {
      setStage('closed');
      return;
    }
    const saved = loadOpenAttempt(slug);
    if (!saved) {
      setStage('gate');
      return;
    }
    try {
      const state = await fetchOpenAttemptState(saved.attemptId, saved.token);
      if (state.status === 'active') {
        setAttempt({
          attemptId: saved.attemptId,
          token: saved.token,
          totalQuestions: saved.totalQuestions,
          questions: saved.questions,
        });
        setResumeAnswered(state.answeredQuestionIds);
        setAnsweredIds(new Set(state.answeredQuestionIds));
        setStage('resume');
        return;
      }
      // finished/abandoned — новая попытка (лимит может не пустить: 409 → closed).
      clearOpenAttempt();
      setStage('gate');
    } catch {
      // Битая запись (токен умер/попытка удалена вместе с квизом) — стартуем заново.
      clearOpenAttempt();
      setStage('gate');
    }
  }, [slug]);

  useEffect(() => {
    void init();
  }, [init]);

  // Старт новой попытки (из gate или «Пройти ещё раз» после финиша).
  const startAttempt = useCallback(
    async (nickname: string) => {
      setStarting(true);
      setStartError(null);
      try {
        const resp = await startOpenAttempt(slug, nickname);
        saveOpenAttempt({
          slug,
          attemptId: resp.attemptId,
          token: resp.token,
          totalQuestions: resp.totalQuestions,
          questions: resp.questions,
        });
        setAttempt({
          attemptId: resp.attemptId,
          token: resp.token,
          totalQuestions: resp.totalQuestions,
          questions: resp.questions,
        });
        setIndex(0);
        setAnsweredIds(new Set());
        setMyAnswers({});
        setFinishResp(null);
        setStage('quiz');
      } catch (e) {
        const status = e instanceof Error && 'status' in e ? (e as { status: number }).status : 0;
        if (status === 404) setStage('notfound');
        else if (status === 409) setStage('closed');
        else if (status === 429) setStartError(t('openQuiz.rateLimited'));
        else if (status === 400) setStartError(t('openQuiz.nickRequired'));
        else setStartError(t('openQuiz.startError'));
      } finally {
        setStarting(false);
      }
    },
    [slug, t],
  );

  function handleGateSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = nick.trim();
    if (!trimmed) {
      setStartError(t('openQuiz.nickRequired'));
      return;
    }
    setNick(trimmed);
    void startAttempt(trimmed);
  }

  /** Resume «Продолжить»: позиция = первый неотвеченный вопрос (или последний). */
  function handleResume(): void {
    if (!attempt) return;
    const total = attempt.totalQuestions;
    let pos = total - 1;
    for (let i = 0; i < total; i++) {
      if (!answeredIds.has(i + 1)) {
        pos = i;
        break;
      }
    }
    setIndex(pos);
    setStage('quiz');
  }

  /** «Начать заново» из resume-оффера: новая попытка (токен прежней затирается). */
  function handleResumeRestart(): void {
    clearOpenAttempt();
    setAttempt(null);
    setAnsweredIds(new Set());
    setMyAnswers({});
    setStage('gate');
  }

  // Сабмит ответа: оптимистичная отметка answered, fire-and-forget с мягкой
  // ошибкой; идемпотентность сервера делает повторы безопасными (§C6.2).
  function handleSubmitAnswer(answer: PlayerAnswer): void {
    if (!attempt) return;
    const q = attempt.questions[index];
    if (!q) return;
    setSubmitError(null);
    setAnsweredIds((prev) => new Set(prev).add(q.questionId));
    setMyAnswers((prev) => ({ ...prev, [q.questionId]: answer }));
    const body = toAnswerBody(answer);
    submitOpenAnswer(attempt.attemptId, attempt.token, { questionId: q.questionId, ...body })
      .then(() => {
        // accepted: сервер подтвердил приём (повтор — тоже accepted).
      })
      .catch((e: unknown) => {
        const status = e instanceof Error && 'status' in e ? (e as { status: number }).status : 0;
        if (status === 404 || status === 401) {
          // Попытка/квиз удалены на лету (RC6) или токен умер — новая попытка.
          clearOpenAttempt();
          setStage('gone');
          return;
        }
        if (status === 409) {
          // attempt_finished: показываем итоги немедленно (финиш идемпотентен).
          void handleFinish();
          return;
        }
        setSubmitError(t('openQuiz.submitError'));
        setAnsweredIds((prev) => {
          const next = new Set(prev);
          next.delete(q.questionId);
          return next;
        });
      });
  }

  // Финиш: последний свайп/кнопка «Завершить» → итоги + reveal (единственная точка).
  async function handleFinish(): Promise<void> {
    if (!attempt || finishing) return;
    setFinishing(true);
    setSubmitError(null);
    try {
      const resp = await finishOpenAttempt(attempt.attemptId, attempt.token);
      clearOpenAttempt();
      setFinishResp(resp);
      setStage('finish');
    } catch (e) {
      const status = e instanceof Error && 'status' in e ? (e as { status: number }).status : 0;
      if (status === 404 || status === 401) {
        clearOpenAttempt();
        setStage('gone');
      } else {
        setSubmitError(t('openQuiz.submitError'));
      }
    } finally {
      setFinishing(false);
    }
  }

  function goNext(): void {
    if (!attempt) return;
    if (index >= attempt.totalQuestions - 1) {
      void handleFinish();
      return;
    }
    setIndex((i) => i + 1);
  }

  function goPrev(): void {
    setIndex((i) => Math.max(0, i - 1));
  }

  // Свайп (touch): горизонтальная дельта ≥ 50px за ≤ 500 мс; вертикальный скролл
  // не мешает (требуем горизонтальность: |dx| > |dy|). preventDefault НЕ зовём.
  function handleTouchStart(e: React.TouchEvent<HTMLDivElement>): void {
    const touch = e.changedTouches[0];
    if (touch) {
      touchStart.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
    }
  }

  function handleTouchEnd(e: React.TouchEvent<HTMLDivElement>): void {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const touch = e.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const dt = Date.now() - start.t;
    if (dt > SWIPE_MAX_MS) return;
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) goNext();
    else goPrev();
  }

  // Клавиатура: стрелки на области деки; НЕ перехватываем, когда фокус в поле
  // ввода (open/word textarea/input, match select — §C6.2).
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    const target = e.target as HTMLElement | null;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    ) {
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    }
  }

  // Фокус-менеджмент: при перелистывании фокус на новую карточку (a11y).
  useEffect(() => {
    if (stage === 'quiz') cardRef.current?.focus();
  }, [index, stage]);

  // ── Экраны-заглушки ─────────────────────────────────────────────────────────

  if (stage === 'loading') {
    return (
      <main className="card card--page open-run">
        <p className="muted" data-testid="open-run-loading">
          {t('common.loading')}
        </p>
      </main>
    );
  }

  if (stage === 'notfound') {
    return (
      <main className="card card--page open-run">
        <h1 className="block-title">{t('openQuiz.notFoundTitle')}</h1>
        <p className="muted" data-testid="open-run-notfound">
          {t('openQuiz.notFoundText')}
        </p>
        <Link to="/" className="btn btn--ghost">
          {t('nav.toMain')}
        </Link>
      </main>
    );
  }

  if (stage === 'closed') {
    return (
      <main className="card card--page open-run">
        <h1 className="block-title">{meta ? meta.title : ''}</h1>
        <p className="muted" data-testid="open-run-closed">
          {t('openQuiz.closedText')}
        </p>
        <Link to="/" className="btn btn--ghost">
          {t('nav.toMain')}
        </Link>
      </main>
    );
  }

  if (stage === 'gone') {
    return (
      <main className="card card--page open-run">
        <h1 className="block-title">{t('openQuiz.goneTitle')}</h1>
        <p className="muted" data-testid="open-run-gone">
          {t('openQuiz.goneText')}
        </p>
        <div className="open-run__actions">
          <button type="button" className="btn" onClick={() => void init()}>
            {t('openQuiz.retry')}
          </button>
          <Link to="/" className="btn btn--ghost">
            {t('nav.toMain')}
          </Link>
        </div>
      </main>
    );
  }

  if (stage === 'error') {
    return (
      <main className="card card--page open-run">
        <p className="error" role="alert" data-testid="open-run-error">
          {t('openQuiz.startError')}
        </p>
        <div className="open-run__actions">
          <button type="button" className="btn" onClick={() => void init()}>
            {t('openQuiz.retry')}
          </button>
          <Link to="/" className="btn btn--ghost">
            {t('nav.toMain')}
          </Link>
        </div>
      </main>
    );
  }

  if (stage === 'resume' && attempt) {
    return (
      <main className="card card--page open-run" aria-labelledby="open-resume-title">
        <h1 id="open-resume-title" className="block-title">
          {meta ? meta.title : ''}
        </h1>
        <p className="muted" data-testid="open-run-resume">
          {t('openQuiz.resumeText', {
            answered: resumeAnswered.length,
            total: attempt.totalQuestions,
          })}
        </p>
        <div className="open-run__actions">
          <button
            type="button"
            className="cta"
            onClick={handleResume}
            data-testid="open-run-resume-continue"
          >
            {t('openQuiz.resumeContinue')}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={handleResumeRestart}
            data-testid="open-run-resume-restart"
          >
            {t('openQuiz.resumeRestart')}
          </button>
        </div>
      </main>
    );
  }

  if (stage === 'gate') {
    return (
      <main className="card card--page open-run" aria-labelledby="open-gate-title">
        <h1 id="open-gate-title" className="block-title">
          {meta ? meta.title : ''}
        </h1>
        <p className="muted" data-testid="open-run-meta">
          {t('openQuiz.questionsCount', { n: meta ? meta.questionCount : 0 })}
        </p>
        <form className="entry" onSubmit={handleGateSubmit} noValidate>
          <label className="field-label" htmlFor="open-nick">
            {t('openQuiz.nickLabel')}
          </label>
          <input
            id="open-nick"
            className="text-input"
            type="text"
            maxLength={30}
            placeholder={t('openQuiz.nickPlaceholder')}
            value={nick}
            onChange={(e) => {
              setNick(e.target.value);
              setStartError(null);
            }}
            data-testid="open-run-nick"
            autoFocus
          />
          <button
            type="submit"
            className="cta"
            disabled={starting || nick.trim().length === 0}
            data-testid="open-run-start"
          >
            {starting ? t('common.loading') : t('openQuiz.start')}
          </button>
          {startError ? (
            <p className="error" role="alert" data-testid="open-run-start-error">
              {startError}
            </p>
          ) : null}
        </form>
      </main>
    );
  }

  if (stage === 'finish' && finishResp && attempt) {
    return (
      <main
        className="card card--page open-run open-run--finish"
        aria-labelledby="open-finish-title"
      >
        <h1 id="open-finish-title" className="block-title">
          {t('openQuiz.finishTitle')}
        </h1>
        <p className="open-run__score" data-testid="open-finish-score">
          {t('openQuiz.yourScore', { nick: finishResp.nickname, score: finishResp.score })}
        </p>
        <p className="muted" data-testid="open-finish-correct">
          {t('openQuiz.correctOf', {
            correct: finishResp.correctCount,
            total: finishResp.totalQuestions,
          })}
        </p>
        <ul className="open-finish__list" data-testid="open-finish-list">
          {finishResp.questions.map((fq) => {
            const q = attempt.questions[fq.questionId - 1];
            if (!q) return null;
            const mine = answerText(q, myAnswers[fq.questionId]);
            const reveal = revealText(t, q, fq.reveal);
            return (
              <li
                key={fq.questionId}
                className={`open-finish__item${fq.correct ? ' open-finish__item--ok' : ' open-finish__item--wrong'}`}
                data-testid="open-finish-item"
              >
                <div className="open-finish__head">
                  <span
                    className="open-finish__verdict"
                    aria-label={fq.correct ? t('openQuiz.correctLabel') : t('openQuiz.wrongLabel')}
                  >
                    {fq.correct ? '✓' : '✗'}
                  </span>
                  <span className="open-finish__title">
                    {fq.questionId}. <FormulaText text={q.title} />
                  </span>
                  <span className="open-finish__points">+{fq.points}</span>
                </div>
                {mine !== null ? (
                  <p className="open-finish__mine">
                    {t('openQuiz.yourAnswerLabel')}: <FormulaText text={mine} />
                  </p>
                ) : null}
                {reveal ? (
                  <p className="open-finish__reveal">
                    <FormulaText text={reveal} />
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
        <div className="open-run__actions">
          {meta && meta.status === 'open' ? (
            <button
              type="button"
              className="cta"
              onClick={() => void startAttempt(finishResp.nickname)}
              data-testid="open-finish-again"
            >
              {t('openQuiz.tryAgain')}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => navigate('/')}
            data-testid="open-finish-home"
          >
            {t('nav.toMain')}
          </button>
        </div>
      </main>
    );
  }

  // stage === 'quiz'
  if (!attempt) {
    return (
      <main className="card card--page open-run">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  const q = attempt.questions[index];
  const total = attempt.totalQuestions;
  const isLast = index >= total - 1;

  const bodyProps: QuestionBodyProps = {
    question: toQuestionPayload(q, index, total),
    variant: 'answer',
    myAnswer: myAnswers[q.questionId] ?? null,
    reveal: null,
    counts: null,
    liveWords: null,
    onAnswer: handleSubmitAnswer,
  };

  return (
    <main className="card card--page open-run" aria-labelledby="open-quiz-title">
      <h1 id="open-quiz-title" className="sr-only">
        {meta ? meta.title : ''}
      </h1>
      <div className="open-run__head">
        <span className="open-run__progress" aria-live="polite" data-testid="open-run-progress">
          {t('openQuiz.progress', { index: index + 1, total })}
        </span>
        <div className="open-run__dots" role="group" aria-label={t('openQuiz.dotsAria')}>
          {Array.from({ length: total }, (_, i) => (
            <span
              key={i}
              className={`open-run__dot${i === index ? ' open-run__dot--current' : ''}${
                answeredIds.has(i + 1) ? ' open-run__dot--answered' : ''
              }`}
              aria-current={i === index ? 'step' : undefined}
              data-testid={`open-run-dot-${i + 1}`}
            />
          ))}
        </div>
      </div>

      <div
        className="open-run__deck"
        role="group"
        aria-roledescription={t('openQuiz.deckAria')}
        tabIndex={0}
        ref={cardRef}
        onKeyDown={handleKeyDown}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        data-testid="open-run-deck"
      >
        <div className="open-card" aria-label={t('openQuiz.cardAria', { index: index + 1, total })}>
          {q.image ? (
            <img
              className="q-image"
              loading="lazy"
              decoding="async"
              src={q.image}
              alt={q.title}
              // Устойчивость к протухшей подписи secure_link (RC7): убираем битый
              // <img>, не роняя карточку (паттерн QuestionView).
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : null}
          <h2 className="q-title">
            <FormulaText text={q.title} />
          </h2>
          {/* Фаза 16 (B3): блок кода вопроса открытого квиза — отдельная точка
              вставки (соло-режим без QuestionView); CodeBlock сам лениво грузит
              грамматику своего языка (state.langs в соло не приходит). */}
          {q.code ? <CodeBlock code={q.code} lang={q.codeLang} /> : null}
          {renderSoloBody(q.kind, bodyProps)}
          {answeredIds.has(q.questionId) ? (
            <p className="q-hint q-hint--ok" data-testid="open-run-answered">
              {t('openQuiz.answeredMark')}
            </p>
          ) : null}
          {submitError ? (
            <p className="error" role="alert" data-testid="open-run-submit-error">
              {submitError}
            </p>
          ) : null}
        </div>
      </div>

      <div className="open-run__nav">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={goPrev}
          disabled={index === 0}
          aria-label={t('openQuiz.prev')}
          data-testid="open-run-prev"
        >
          ←
        </button>
        <button
          type="button"
          className={isLast ? 'cta' : 'btn'}
          onClick={goNext}
          disabled={finishing}
          aria-label={isLast ? t('openQuiz.finishBtn') : t('openQuiz.next')}
          data-testid="open-run-next"
        >
          {isLast ? t('openQuiz.finishBtn') : '→'}
        </button>
      </div>
    </main>
  );
}

/** Run-заглушка соло-режима (§3.6 решение Д): run — кооперативный спектакль
 *  живой комнаты; соло-забег без зрителей/соперников теряет геймплей. */
function RunSoloUnsupported(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="unknown-kind-view" data-testid="open-run-solo-unsupported" role="note">
      <p className="unknown-kind-view__icon" aria-hidden="true">
        🏃
      </p>
      <p className="unknown-kind-view__title">{t('openQuiz.runSoloUnsupportedTitle')}</p>
      <p className="unknown-kind-view__body">{t('openQuiz.runSoloUnsupported')}</p>
    </div>
  );
}

function renderSoloBody(kind: string, props: QuestionBodyProps): JSX.Element {
  switch (kind) {
    case 'poll':
      return <PollView {...props} />;
    case 'open':
      return <OpenView {...props} />;
    case 'match':
      return <MatchView {...props} />;
    case 'order':
      return <OrderView {...props} />;
    case 'word':
      return <WordView {...props} />;
    case 'multi':
      return <MultiView {...props} />;
    case 'run':
      // Соло-режим run НЕ поддерживается (решение Д §3.6): сервер присылает
      // kindData-заглушку {corners, soloUnsupported}; вопрос пропускается с
      // 0 очков (ответа нет — как у не-ответивших choice).
      return <RunSoloUnsupported />;
    default:
      return <ChoiceView {...props} />;
  }
}
